import Modal from '@components/Modal';
import { useModal } from '@components/Modal/ModalProvider';
import useUploadFiles from '@hooks/Files/useUploadFiles';
import {
  ChangeEvent,
  DragEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import styles from './FileUploadModalContent.module.scss';
import { ReactComponent as CardiVue } from 'img/autoseg_logo.svg';
import { ReactComponent as Export } from 'img/menu_export.svg';
import { ReactComponent as Plus } from 'img/plus.svg';
import { ICON_MAP } from '@components/Icons/Icons';

interface FileUploadModalContentProps {
  closeModal: () => void;
}

interface FileSystemEntryWithMeta {
  isDirectory: boolean;
  isFile: boolean;
}

interface FileSystemFileEntryWithMeta extends FileSystemEntryWithMeta {
  isFile: true;
  file: (successCallback: (file: File) => void) => void;
}

interface FileSystemDirectoryEntryWithMeta extends FileSystemEntryWithMeta {
  isDirectory: true;
  createReader: () => {
    readEntries: (successCallback: (entries: FileSystemEntryWithMeta[]) => void) => void;
  };
}

interface DataTransferItemWithEntry extends DataTransferItem {
  webkitGetAsEntry?: () => FileSystemEntryWithMeta | null;
}

function FileUploadModalContent({ closeModal }: FileUploadModalContentProps) {
  const inputFileRef = useRef<HTMLInputElement>(null);
  const inputFolderRef = useRef<HTMLInputElement>(null);
  const CloseIcon = ICON_MAP['close'];

  const [files, setFiles] = useState<File[] | null>([]);
  const [isDragOver, setIsDragOver] = useState(false);

  const { upload } = useUploadFiles();

  useEffect(() => {
    if (!inputFolderRef.current) return;
    inputFolderRef.current.setAttribute('webkitdirectory', '');
  }, []);

  const handleFileClick = () => {
    inputFileRef.current?.click();
  };
  const handleFolderClick = () => {
    inputFolderRef.current?.click();
  };

  const getFileUniqueKey = (file: File) => {
    const fileWithPath = file as File & { path?: string };
    return (
      fileWithPath.path?.toLowerCase() ??
      `${file.name.toLowerCase()}__${file.size}__${file.lastModified}`
    );
  };

  const mergeUniqueFiles = (prev: File[] | null, next: File[]) => {
    const merged = new Map<string, File>();
    (prev ?? []).forEach((file) => {
      merged.set(getFileUniqueKey(file), file);
    });
    next.forEach((file) => {
      merged.set(getFileUniqueKey(file), file);
    });
    return Array.from(merged.values());
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const result = event.target.files;
    if (result) {
      const selectedFiles = Array.from(result);
      setFiles((prev) => mergeUniqueFiles(prev, selectedFiles));
      event.target.value = '';
      return;
    }
    setFiles(result);
    event.target.value = '';
  };

  const handleDeleteFile = (idx: number) => {
    setFiles((prev) => {
      if (!prev || prev.length === 0) return prev;
      return [...prev.slice(0, idx), ...prev.slice(idx + 1)];
    });
  };

  const handleUploadFile = async () => {
    if (!files || files.length === 0) return;
    upload({ files });
    setFiles([]);

    if (inputFileRef.current) inputFileRef.current.value = '';
    if (inputFolderRef.current) inputFolderRef.current.value = '';
  };

  const readDirectoryEntries = (
    dirEntry: FileSystemDirectoryEntryWithMeta,
  ): Promise<FileSystemEntryWithMeta[]> =>
    new Promise((resolve) => {
      const reader = dirEntry.createReader();
      const entries: FileSystemEntryWithMeta[] = [];

      const readBatch = () => {
        reader.readEntries((batch) => {
          if (!batch || batch.length === 0) {
            resolve(entries);
            return;
          }
          entries.push(...batch);
          readBatch();
        });
      };

      readBatch();
    });

  const readFileFromEntry = (
    fileEntry: FileSystemFileEntryWithMeta,
  ): Promise<File> =>
    new Promise((resolve) => {
      fileEntry.file((file) => resolve(file));
    });

  const collectFilesFromEntry = async (
    entry: FileSystemEntryWithMeta | null,
  ): Promise<File[]> => {
    if (!entry) return [];

    if (entry.isFile) {
      const file = await readFileFromEntry(entry as FileSystemFileEntryWithMeta);
      return [file];
    }

    if (!entry.isDirectory) return [];

    const children = await readDirectoryEntries(
      entry as FileSystemDirectoryEntryWithMeta,
    );
    const nestedFiles = await Promise.all(
      children.map((child) => collectFilesFromEntry(child)),
    );
    return nestedFiles.flat();
  };

  const handleDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    const dropped: File[] = [];
    const items = event.dataTransfer.items
      ? Array.from(event.dataTransfer.items)
      : [];

    if (items.length > 0) {
      for (const item of items) {
        const itemWithEntry = item as DataTransferItemWithEntry;
        const entry = itemWithEntry.webkitGetAsEntry?.() ?? null;

        if (entry) {
          const filesFromEntry = await collectFilesFromEntry(entry);
          dropped.push(...filesFromEntry);
          continue;
        }

        const file = item.getAsFile();
        if (file) dropped.push(file);
      }
    } else {
      dropped.push(...Array.from(event.dataTransfer.files ?? []));
    }

    setFiles((prev) => mergeUniqueFiles(prev, dropped));
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isDragOver) setIsDragOver(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const dropZoneClassName = `${styles.contentItem} ${isDragOver ? styles.dragActive : ''}`;

  return (
    <div className={`${styles.importModal}`}>
      <div className={`${styles.importHeader}`}>
        <CardiVue className={`${styles.headerLogo}`} />
        <span className={`${styles.headerTitle}`}>Import Files</span>
        <div className={`${styles.headerCloseButton}`}>
          <CloseIcon className={`${styles.closeIcon}`} onClick={closeModal} />
        </div>
      </div>
      <div className={`${styles.importContent}`}>
        {files && files.length > 0 ? (
          <div
            className={dropZoneClassName}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <div className={`${styles.listFrame}`}>
              <div className={`${styles.list}`}>
                {files.map((file, idx) => (
                  <div className={`${styles.items}`} key={file.name}>
                    <span>{file.name}</span>
                    <div className={`${styles.itemclose}`}>
                      <CloseIcon
                        onClick={() => handleDeleteFile(idx)}
                      />
                    </div>{' '}
                  </div>
                ))}
              </div>
            </div>

            <div className={`${styles.addContainer}`}>
              <button onClick={handleFileClick}>
                <Plus />
                <span>Add more files</span>
              </button>
            </div>
          </div>
        ) : (
          <div
            className={dropZoneClassName}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <div className={`${styles.importIcon}`}>
              <Export />
            </div>
            <div className={`${styles.importTextContainer}`}>
              <div className={`${styles.importText}`}>
                <span>
                  Please select the file or folder you want to upload.
                </span>
              </div>
            </div>
            <div className={`${styles.importButtons}`}>
              <button onClick={handleFileClick}>
                <span>Select Files</span>
              </button>
              <button onClick={handleFolderClick}>
                <span>Select Folder</span>
              </button>
            </div>
          </div>
        )}
        <div className={`${styles.contentActions}`}>
          <button onClick={closeModal}>
            <span>Cancle</span>
          </button>
          <div className={`${styles.frame}`}>
            <button onClick={handleUploadFile}>
              <Export />
              <span>Upload{files && files.length > 0 && `(${files.length})`}</span>
            </button>
          </div>
        </div>
      </div>
      <input
        type="file"
        ref={inputFileRef}
        multiple
        hidden
        onChange={handleChange}
      />
      <input
        type="file"
        ref={inputFolderRef}
        multiple
        hidden
        onChange={handleChange}
      />
    </div>
  );
}

export default function useFileUploadModal() {
  const { openModal, closeModal } = useModal();

  const handleOpenFileUploadModal = useCallback(() => {
    const id = openModal(
      <Modal
        type="page"
        useHeader={true}
        close={() => closeModal(id)}
        description={
          <FileUploadModalContent closeModal={() => closeModal(id)} />
        }
      />,
    );
  }, [openModal]);

  return { handleOpenFileUploadModal };
}
